import type { GameState } from '../types/game-state.ts';
import type { Move } from '../types/move.ts';
import { findAllPlacements, findPlaceableSlots } from './placement.ts';
import { rotationCandidates } from './rotation.ts';

export type BotDifficulty = 'easy';

export type BotDecisionOptions = {
  difficulty?: BotDifficulty;
};

/**
 * 봇의 다음 수를 결정.
 *
 * 반환:
 *  - 둘 수 있는 수가 있으면 Move
 *  - 둘 수 있는 수가 없으면 null (호출자가 forceAutoPass로 턴 종료)
 *
 * easy 휴리스틱:
 *  - holding 없음: pool에 즉시 배치 가능한 타일이 있으면 TAKE_FROM_POOL,
 *    아니면 deck 잔여 시 DRAW_FROM_DECK, 둘 다 없으면 null
 *  - holding 있음 (source=DECK): 배치 가능하면 PLACE, 아니면 DISCARD_TO_POOL
 *  - holding 있음 (source=POOL): 반드시 배치 — TAKE_FROM_POOL 호출 시 placeable 검증되었음
 *
 * 호출 시점:
 *  - state.status === 'IN_PROGRESS'
 *  - state.players[state.currentPlayerIdx].id === botId
 */
export function decideBotMove(
  state: GameState,
  botId: string,
  _options: BotDecisionOptions = {},
): Move | null {
  const current = state.players[state.currentPlayerIdx];
  if (!current || current.id !== botId) {
    throw new Error(`not bot's turn: current=${current?.id ?? 'none'}, bot=${botId}`);
  }

  if (state.holding === null) {
    const poolPick = pickPoolTile(state, current.lane);
    if (poolPick !== null) {
      return { type: 'TAKE_FROM_POOL', tileId: poolPick };
    }
    if (state.center.deck.length > 0) {
      return { type: 'DRAW_FROM_DECK' };
    }
    return null;
  }

  const placements = findAllPlacements(current.lane, {
    number: state.holding.number,
    resolvedNumber: state.holding.resolvedNumber,
  });
  if (placements.length > 0) {
    const pick = placements[0];
    if (pick) {
      return { type: 'PLACE', resolvedNumber: pick.resolvedNumber, targetIdx: pick.targetIdx };
    }
  }
  if (state.holding.source === 'POOL') {
    // 풀에서 가져왔는데 둘 곳이 없음 — pickPoolTile 검증 위반. 방어적으로 null.
    return null;
  }
  return { type: 'DISCARD_TO_POOL' };
}

function pickPoolTile(state: GameState, lane: GameState['players'][number]['lane']): string | null {
  for (const tile of state.center.openPool) {
    const candidates =
      tile.resolvedNumber !== null ? [tile.resolvedNumber] : rotationCandidates(tile.number);
    for (const n of candidates) {
      if (findPlaceableSlots(lane, n).length > 0) {
        return tile.id;
      }
    }
  }
  return null;
}
